import { selectUser } from "@/app/auth/auth.slice";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ROLE_NAMES } from "../organisms/BranchSideBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Permission } from "@/shared/hooks/usePermission";

/**
 * @param children Children React
 * @returns ReactNode
 * @description Component designed to protect the /branchs route.
 * Redirects the user to their own branch, or if they have the
 * 'multi_centro' permission, grants access to the full list of
 * branches associated with their GymId.
 *
 */

export const ProtectedGym = ({ children }: { children: React.ReactNode }) => {
  const [isValidatingAccess, setIsValidatingAccess] = useState<boolean>(true);
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const validateAccess = useCallback(() => {
    try {
      if (!user?.role || !user?.permissions || !user?.branch) {
        return;
      }

      if (user.role === ROLE_NAMES.SUPER_ADMIN) {
        setIsValidatingAccess(false);
        return;
      }

      const canAccessGyms = user.permissions.includes(Permission.MULTI_CENTRO);
      const currentPath = location.pathname;
      const branchPath = `/branchs/${user.branch.id}`;
      const gymsPath = `/branchs?gymId=${user.branch.gymId}`;

      if (!canAccessGyms && currentPath !== branchPath) {
        navigate(branchPath, { replace: true });
        setIsValidatingAccess(false); // 👈 evita el loop
        return;
      }

      if (canAccessGyms && currentPath === "/branchs" && !location.search) {
        navigate(gymsPath, { replace: true });
        setIsValidatingAccess(false); // 👈 evita el loop
        return;
      }

      setIsValidatingAccess(false);
    } catch (error) {
      console.error("Error validating access:", error);
      setIsValidatingAccess(false);
    }
  }, [user]);

  // 👇 así React asegura que se ejecuta cuando debe
  useEffect(() => {
    if (!user) return;
    validateAccess();
  }, [validateAccess]);

  // NOW the early return after ALL hooks have been called
  if (isValidatingAccess) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <FontAwesomeIcon icon={faSpinner} spin className="text-6xl" />
      </div>
    );
  }

  return children;
};
